import React from "react";

import { Image } from "../common/Image";
import { IStringField } from "../models/formFields.interfaces";
import { IFormFieldState } from "./AbstractFormField";
import { AbstractInput } from "./AbstractInput";

// This is a temporary measure to allow for SSR of FormInputs, @zlebar
// TODO(ssr, LEVEL 6 out of 10): Migrate to functional component
// Complexity: Text input without Cleave.js formatting (temporary SSR workaround). Has byte-length
// validation and truncation (maxBytes), image display support, componentDidMount lifecycle, onChange
// handler with truncation logic, refs for DOM access. Similar to FormInput but simpler (no formatter).
// Migration: Create useByteLengthValidator hook for byte truncation logic. Convert image display to
// separate component. Use useFormField hook (from migrated AbstractFormField) for base functionality.
// Replace componentDidMount with useEffect (though currently just calls super). Replace inputElem ref
// with useRef. Consider merging with FormInput migration if formatting can be made SSR-compatible.
export class FormInputWithoutFormatting<T extends string> extends AbstractInput<
    T,
    IStringField<T>,
    IFormFieldState
> {
    public static defaultProps = {
        type: "text",
    };

    public state: IFormFieldState = {
        focused: false,
    };

    public onChange(event: React.FormEvent<HTMLInputElement>): void {
        if (!event.currentTarget) {
            return;
        }

        // If maxBytes property is provided, check the current value of the input and truncate it to stay within the size limit.
        if (this.props.maxBytes) {
            const original = event.currentTarget.value;
            const truncated = this.truncateStringByBytes(
                original,
                this.props.maxBytes,
            );
            if (original !== truncated) {
                event.currentTarget.value = truncated;
            }
        }

        super.onChange(event);
    }

    public componentDidMount(): void {
        super.componentDidMount();
    }

    protected getInputProps() {
        return Object.assign(super.getInputProps(), {
            minLength: this.props.minLength,
            maxLength: this.props.maxLength,
            pattern: this.props.pattern,
        });
    }

    private truncateStringByBytes(original: string, maxBytes: number): string {
        let truncated: string;
        let byteLength: number;
        for (let i = original.length; i >= 0; i--) {
            truncated = original.slice(0, i);
            byteLength = encodeURIComponent(
                truncated.replace(/\d/g, "X"),
            ).replace(/%[A-F\d]{2,6}/g, "U").length;
            if (byteLength <= maxBytes) {
                return truncated;
            }
        }
        throw Error(
            `This should never happen! Somehow an empty string is longer than ${maxBytes} bytes.`,
        );
    }

    protected buildControl(): React.JSX.Element {
        const props = this.getInputProps();
        props.className ??= "form__field__input";
        let image = null;
        if (
            this.props.secureImage &&
            this.props.secureImage.image &&
            this.props.secureImage.image.length > 0 &&
            this.props.secureImage.alt
        ) {
            image = (
                <Image
                    src={this.props.secureImage.image}
                    alt={this.props.secureImage.alt}
                    lazy={false}
                />
            );
        }
        return (
            <>
                <input
                    ref={(ref) => {
                        this.inputElem = ref;
                    }}
                    {...props}
                />
                {image}
            </>
        );
    }
}
